import { createSlice } from '@reduxjs/toolkit';

import initialState from './state';
import {
  setOnboarding,
  setHasReachedConsentForm,
  addVisitedWizardStep,
  setConsentAcceptance,
  updateWorker,
  setTemp,
  addGovernmentDocumentFile,
  setWorkerPIIPinInformation,
  setDbMetadata,
  resetOnboardingState,
  resetTempOnboardingData,
  createWorker,
  createPassword,
  acceptBiometricConsent,
  fetchWorkerDetails,
  uploadGovernmentDocumentFiles,
  deleteGovernmentDocumentFile,
  fetchCertificationGroups,
  fetchAllCertifications,
  addCertification,
  updateCertification,
  assignWorkerToCompanies,
  fetchAssignedCompanies,
  deleteCertification,
  uploadSignatureToS3,
  acceptProject,
  fetchWorkerExistingConsentForms,
  submitConsentAcceptances,
  fetchWorkerConsentAcceptances,
} from './actions';
import { logoutUser } from '../user';
import { getEmptyPiiFieldsFromWorkerDetails } from 'utils/onboarding';

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState: initialState,
  reducers: {
    setOnboarding,
    setHasReachedConsentForm,
    addVisitedWizardStep,
    setConsentAcceptance,
    setTemp,
    addGovernmentDocumentFile,
    setWorkerPIIPinInformation,
    setDbMetadata,
    resetOnboardingState,
    resetTempOnboardingData,
    resetAssignedCompanies: state => {
      state.assignedCompanies = [];
    },
  },
  extraReducers: builder =>
    builder
      .addCase(createWorker.fulfilled, (state, action) => {
        state = {
          ...state,
          ...(action.payload || {}),
          temp: {
            ...state.temp,
            email: action.payload.email,
            mobilePhoneNumber: action.payload.mobilePhoneNumber,
          },
          uploadingDetails: state.uploadingDetails,
        };
        return state;
      })
      .addCase(createPassword.fulfilled, (state, action) => {
        return state;
      })
      .addCase(acceptBiometricConsent.fulfilled, (state, action) => {
        state.signedBiometricConsentForm = true;
        return state;
      })
      .addCase(fetchWorkerDetails.fulfilled, (state, action) => {
        state = {
          ...state,
          temp: {
            ...(state?.temp || {}),
            emptyPiiFields: getEmptyPiiFieldsFromWorkerDetails(action.payload),
          },
          ...(action.payload || {}),
          ...(state.id
            ? {
                certificationGroups: state?.certificationGroups,
                allCertifications: state?.allCertifications,
                existingConsentFormForAllCompanies: state?.existingConsentFormForAllCompanies,
                assignedCompanies: state?.assignedCompanies,
              }
            : {}),
          ...(state?.governmentDocumentFiles?.length ? { governmentDocumentFiles: state.governmentDocumentFiles } : {}),
          uploadingDetails: state.uploadingDetails,
        };
        return state;
      })
      .addCase(updateWorker.fulfilled, (state, action) => {
        state = {
          ...state,
          temp: {
            ...(state?.temp || {}),
            emptyPiiFields: getEmptyPiiFieldsFromWorkerDetails(action.payload),
          },
          ...(action.payload || {}),
          certificationGroups: state?.certificationGroups,
          allCertifications: state?.allCertifications,
          existingConsentFormForAllCompanies: state?.existingConsentFormForAllCompanies,
          assignedCompanies: state?.assignedCompanies,
          uploadingDetails: state.uploadingDetails,
        };
        return state;
      })
      .addCase(uploadGovernmentDocumentFiles.fulfilled, (state, action) => {
        state.governmentDocumentFiles =
          state.governmentDocumentFiles?.map(file => {
            const matchedUpload = action.payload.successes.find(s => s.tempId === file.tempId);
            return matchedUpload
              ? { ...file, id: matchedUpload.id, fileName: matchedUpload.fileName, tempId: null }
              : file;
          }) || [];
        return state;
      })
      .addCase(deleteGovernmentDocumentFile.fulfilled, (state, action) => {
        state.governmentDocumentFiles = action.payload || [];
        return state;
      })
      .addCase(fetchCertificationGroups.fulfilled, (state, action) => {
        state.certificationGroups = action.payload;
        return state;
      })
      .addCase(fetchAllCertifications.fulfilled, (state, action) => {
        state.allCertifications = action.payload;
        return state;
      })
      .addCase(fetchWorkerExistingConsentForms.fulfilled, (state, action) => {
        state.existingConsentFormForAllCompanies = action.payload;
        return state;
      })
      .addCase(addCertification.fulfilled, (state, action) => {
        state.allCertifications = action.payload;
        return state;
      })
      .addCase(updateCertification.fulfilled, (state, action) => {
        state.allCertifications = action.payload;
        return state;
      })
      .addCase(deleteCertification.fulfilled, (state, action) => {
        state.allCertifications = action.payload;
        return state;
      })
      .addCase(fetchAssignedCompanies.fulfilled, (state, action) => {
        state.assignedCompanies = action.payload || [];
      })
      .addCase(assignWorkerToCompanies.fulfilled, (state, action) => {
        const { newAssignedCompanies } = action?.payload || {};
        state.assignedCompanies = [...(state.assignedCompanies || []), ...(newAssignedCompanies || [])];
      })
      .addCase(acceptProject.fulfilled, (state, action) => {
        const { companyId } = action?.payload || {};
        state.assignedCompanies = (state.assignedCompanies || []).map(company => {
          if (company.id === companyId) {
            return { ...company, workerProjectStatus: 1 };
          }
          return company;
        });
      })
      .addCase(uploadSignatureToS3.fulfilled, (state, action) => {
        return state;
      })
      .addCase(submitConsentAcceptances.fulfilled, (state, action) => {
        const submittedIds: string[] = action.meta?.arg?.consentAgreementContentIds || [];
        state.acceptedStandardConsentIds = Array.from(
          new Set([...(state.acceptedStandardConsentIds || []), ...submittedIds]),
        );
        state.temp = { ...state.temp, consentAcceptances: [] };
      })
      .addCase(fetchWorkerConsentAcceptances.fulfilled, (state, action) => {
        if (state.id && action.meta?.arg?.workerId !== state.id) return;
        const { standard = [], custom = [] } = (action.payload as { standard: string[]; custom: string[] }) || {};
        state.acceptedStandardConsentIds = standard;
        state.acceptedCustomConsentIds = custom;
      })
      .addCase(logoutUser.fulfilled, state => {
        state = initialState;
        return state;
      }),
});

export default onboardingSlice;
